import vehicleHistoryChecksText, {
	VEHICLE_HISTORY_CHECK_TEXT_KEYS,
} from 'Components/VehicleHistoryChecks/VehicleHistoryChecks.texts';

import { Honda } from '../../../__tests__/vehicles/vehicles';

import type { VehicleHistoryCheckKeys } from './useVehicleHistoryChecks.types';

const allClearHistoryChecks = {
	completedOnDate: '–',
	financeAgreementsCount: 0,
	hasBeenImported: false,
	hasBeenSalvaged: false,
	hasBeenStolen: false,
	hasBeenWrittenOffByDamage: false,
	hasBeenWrittenOffByTheft: false,
	hasSecurityWatch: false,
	isMileageClocked: false,
	isPlateTransferred: false,
	isVehicleValid: true,
	motExpiryDate: '',
	taxiUse: {},
};

const failureHistoryChecks = {
	completedOnDate: '–',
	financeAgreementsCount: 2,
	hasBeenImported: true,
	hasBeenSalvaged: true,
	hasBeenStolen: true,
	hasBeenWrittenOffByDamage: true,
	hasBeenWrittenOffByTheft: true,
	hasSecurityWatch: true,
	isMileageClocked: true,
	isPlateTransferred: true,
	isVehicleValid: false,
	motExpiryDate: '2022-01-01T00:00:00.000Z',
	taxiUse: {
		dateFrom: '2020-05-04T08:20:52.053+00:00',
		dateTo: '2020-11-04T08:20:52.054+00:00',
		source: 'Transport for London Council',
	},
};

const defaultAllClearVehicle = {
	...Honda,
	historyChecks: allClearHistoryChecks,
};

const defaultFailingVehicle = {
	...Honda,
	historyChecks: failureHistoryChecks,
};

const allClearConfig = {
	financeAgreement: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.financeAgreement,
		text: vehicleHistoryChecksText.financeAgreement.success,
		translateArgs: { agreement: 'agreements', agreementCount: 0 },
	},
	importCheck: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.importCheck,
		text: vehicleHistoryChecksText.importCheck.success,
	},
	insuranceWriteOffDamage: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.insuranceWriteOffDamage,
		text: vehicleHistoryChecksText.insuranceWriteOffDamage.success,
	},
	insuranceWriteOffInsurance: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.insuranceWriteOffInsurance,
		text: vehicleHistoryChecksText.insuranceWriteOffInsurance.success,
	},
	mot: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.mot,
		text: vehicleHistoryChecksText.mot.success,
		translateArgs: { expiryDate: '–' },
	},
	plateTransfer: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.plateTransfer,
		text: vehicleHistoryChecksText.plateTransfer.success,
	},
	salvageCheck: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.salvageCheck,
		text: vehicleHistoryChecksText.salvageCheck.success,
	},
	securityCheck: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.securityCheck,
		text: vehicleHistoryChecksText.securityCheck.success,
	},
	stolen: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.stolen,
		text: vehicleHistoryChecksText.stolen.success,
	},
	taxiCheck: {
		isWarning: false,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.taxiCheck,
		text: vehicleHistoryChecksText.taxiCheck.success,
		translateArgs: { dateFrom: '', dateTo: '', source: '' },
	},
};

const failureConfig = {
	financeAgreement: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.financeAgreement,
		text: 'Has 2 finance agreements recorded on the CAP HPI Asset Register.',
		translateArgs: { agreement: 'agreements', agreementCount: 2 },
	},
	importCheck: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.importCheck,
		text: vehicleHistoryChecksText.importCheck.warning,
	},
	insuranceWriteOffDamage: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.insuranceWriteOffDamage,
		text: vehicleHistoryChecksText.insuranceWriteOffDamage.warning,
	},
	insuranceWriteOffInsurance: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.insuranceWriteOffInsurance,
		text: vehicleHistoryChecksText.insuranceWriteOffInsurance.warning,
	},
	mot: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.mot,
		text: 'MOT certificate expired on 01/01/2022.',
		translateArgs: { expiryDate: '01/01/2022' },
	},
	plateTransfer: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.plateTransfer,
		text: vehicleHistoryChecksText.plateTransfer.warning,
	},
	salvageCheck: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.salvageCheck,
		text: vehicleHistoryChecksText.salvageCheck.warning,
	},
	securityCheck: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.securityCheck,
		text: vehicleHistoryChecksText.securityCheck.warning,
	},
	stolen: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.stolen,
		text: vehicleHistoryChecksText.stolen.warning,
	},
	taxiCheck: {
		isWarning: true,
		key: VEHICLE_HISTORY_CHECK_TEXT_KEYS.taxiCheck,
		text: 'Has been used as a taxi (Transport for London Council, 04/05/2020 - 04/11/2020).',
		translateArgs: { dateFrom: '04/05/2020', dateTo: '04/11/2020', source: 'Transport for London Council' },
	},
};

const allClearResponse = {
	identifierIssues: [],
	limitations: {
		ancestry: [],
		financeAgreements: [],
		hasBeenStolen: false,
		hasBeenWrittenOffByDamage: false,
		hasBeenWrittenOffByTheft: false,
		hasSecurityWatch: false,
		import: {
			isImported: false,
			isNonEu: false,
		},
		isMileageClocked: false,
		isPlateTransferred: false,
		mot: {},
		salvageAuctions: [],
	},
};

const failureResponse = {
	identifierIssues: ['vin_mismatch_hpi', 'vrm_mismatch_vcheck'],
	limitations: {
		ancestry: [
			{
				dateFrom: '2020-05-04T08:20:52.053+00:00',
				dateTo: '2020-11-04T08:20:52.054+00:00',
				recordType: 'Other',
				source: 'Transport for London Council',
			},
			{
				dateFrom: failureHistoryChecks.taxiUse.dateFrom,
				dateTo: failureHistoryChecks.taxiUse.dateTo,
				recordType: 'Taxi',
				source: failureHistoryChecks.taxiUse.source,
			},
		],
		financeAgreements: [
			{
				company: 'BMW',
				phone: '+1',
				recordedAgainst: 'VRM',
				reference: 'ref1',
			},
			{
				company: 'Bank of America',
				phone: '+2',
				recordedAgainst: 'VIN',
				reference: 'ref2',
			},
		],
		hasBeenStolen: true,
		hasBeenWrittenOffByDamage: true,
		hasBeenWrittenOffByTheft: true,
		hasSecurityWatch: true,
		import: {
			isImported: true,
			isNonEu: false,
		},
		isMileageClocked: true,
		isPlateTransferred: true,
		mot: {
			expiry: failureHistoryChecks.motExpiryDate,
			isInvalid: true,
		},
		salvageAuctions: [
			{
				category: 'Totalled',
				description: 'Damage to this vehicle is rear end and side',
				location: 'London',
				saleDate: '2020-01-05T15:13:11.984+07:00',
				url: 'http://motorway.co.uk',
			},
		],
	},
};

export const VEHICLE_HISTORY_CHECKS_DISPLAY_ORDER: Readonly<VehicleHistoryCheckKeys[]> = Object.freeze([
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.importCheck,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.salvageCheck,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.taxiCheck,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.mot,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.stolen,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.financeAgreement,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.securityCheck,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.insuranceWriteOffDamage,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.insuranceWriteOffInsurance,
	VEHICLE_HISTORY_CHECK_TEXT_KEYS.plateTransfer,
]);

export const VEHICLE_HISTORY_TEST_CONFIG = Object.freeze({
	allClearConfig,
	allClearResponse,
	allClearVehicle: defaultAllClearVehicle,
	failureConfig,
	failureResponse,
	failureVehicle: defaultFailingVehicle,
});
